import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Button, ButtonGroup, HStack, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-big-calendar';
import { useSearchParams } from 'react-router-dom';
import { translate } from '../../../i18n';

export type CalendarView = 'day' | 'week';

interface CalendarNavigationProps {
  onViewChange?: (view: View) => void;
  onDateBoundsChange?: (start: Date, end: Date) => void;
  onCurrentDateChange?: (date: Date) => void;
  initialView?: CalendarView;
  initialDate?: Date;
}

function parseViewParam(param: string | null, fallback: CalendarView): CalendarView {
  return param === 'day' || param === 'week' ? param : fallback;
}

function parseDateParam(param: string | null, fallback: Date): Date {
  if (!param) return fallback;
  const parsed = new Date(param);
  return isNaN(parsed.getTime()) ? fallback : parsed;
}

export const CalendarNavigation = ({
  onViewChange,
  onDateBoundsChange,
  onCurrentDateChange,
  initialView = 'week',
  initialDate = new Date()
}: CalendarNavigationProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState<CalendarView>(
    parseViewParam(searchParams.get('view'), initialView)
  );
  const [currentDate, setCurrentDate] = useState<Date>(
    parseDateParam(searchParams.get('date'), initialDate)
  );
  const lastNotifiedBounds = useRef<string>('');

  // Calculate date boundaries based on current view and date
  const dateBounds = useMemo((): [Date, Date] => {
    const baseDate = new Date(currentDate);

    if (currentView === 'day') {
      const start = new Date(baseDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(baseDate);
      end.setHours(23, 59, 59, 999);

      return [start, end];
    } else { // week view
      const dayOfWeek = baseDate.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Make Monday the first day

      const start = new Date(baseDate);
      start.setDate(baseDate.getDate() + mondayOffset);
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      return [start, end];
    }
  }, [currentDate, currentView]);

  // Notify parent components when bounds change (with duplicate prevention)
  useEffect(() => {
    const [start, end] = dateBounds;
    const boundsKey = `${start.getTime()}-${end.getTime()}`;

    // Only call if bounds actually changed
    if (lastNotifiedBounds.current !== boundsKey) {
      lastNotifiedBounds.current = boundsKey;
      onDateBoundsChange?.(start, end);
      onCurrentDateChange?.(start);
    }
  }, [dateBounds, onDateBoundsChange, onCurrentDateChange]);

  // Sync view and date to URL search params
  useEffect(() => {
    const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD
    setSearchParams((prev) => {
      prev.set('view', currentView);
      prev.set('date', dateStr);
      return prev;
    }, { replace: true });
  }, [currentView, currentDate, setSearchParams]);

  // Notify parent when view changes
  useEffect(() => {
    onViewChange?.(currentView as View);
  }, [currentView, onViewChange]);

  const handleViewChange = (view: CalendarView) => {
    setCurrentView(view);
  };

  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'day') {
      newDate.setDate(currentDate.getDate() - 1);
    } else {
      newDate.setDate(currentDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'day') {
      newDate.setDate(currentDate.getDate() + 1);
    } else {
      newDate.setDate(currentDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const formatDateRange = () => {
    const [start, end] = dateBounds;

    if (currentView === 'day') {
      return start.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      const startStr = start.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
      const endStr = end.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      return `${startStr} - ${endStr}`;
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <HStack spacing={4} justify="space-between" w="100%" mb={4}>
      {/* View Toggle */}
      <ButtonGroup isAttached variant="outline">
        <Button
          size="sm"
          colorScheme={currentView === 'day' ? 'brand' : 'gray'}
          onClick={() => handleViewChange('day')}
        >
          {translate('calendar.toolbar.day')}
        </Button>
        <Button
          size="sm"
          colorScheme={currentView === 'week' ? 'brand' : 'gray'}
          onClick={() => handleViewChange('week')}
        >
          {translate('calendar.toolbar.week')}
        </Button>
      </ButtonGroup>

      {/* Date Navigation */}
      <HStack spacing={2}>
        <Button
          size="sm"
          variant="outline"
          onClick={navigatePrevious}
          leftIcon={<ChevronLeftIcon />}
        >
          {translate('calendar.toolbar.previous')}
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={goToToday}
        >
          {translate('calendar.toolbar.today')}
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={navigateNext}
          rightIcon={<ChevronRightIcon />}
        >
          {translate('calendar.toolbar.next')}
        </Button>
      </HStack>

      {/* Current Date Range Display */}
      <Text fontSize="md" fontWeight="medium" minW="200px" textAlign="right">
        {formatDateRange()}
      </Text>
    </HStack>
  );
};

export default CalendarNavigation;
